import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Stack,
  Autocomplete,
  IconButton,
  Tooltip,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { PartPayment, PartPaymentFormData } from '../../types/payment';
import { ActiveWorker } from '../../types/workerAssignment';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import api from '../../services/api';
import { makeSafeGridColumns, safeValueGetter } from '../../utils/dataGridHelpers';

interface WorkerPayment {
  worker_id: string;
  amount: number;
  availableAmount: number;
}

const initialFormData: PartPaymentFormData = {
  payment_date: new Date().toISOString().split('T')[0],
  notes: '',
};

export default function PartPayments() {
  const { user } = useAuth();
  const { canDelete } = usePermissions();
  const [payments, setPayments] = useState<PartPayment[]>([]);
  const [activeWorkers, setActiveWorkers] = useState<ActiveWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<PartPaymentFormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [workerPayments, setWorkerPayments] = useState<WorkerPayment[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<number | null>(null);

  const getColumns = (): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      { field: 'worker_id', headerName: 'Worker ID', width: 130 },
      { 
        field: 'worker',
        headerName: 'Worker Name', 
        width: 150,
        renderCell: (params: any) => {
          console.log('Worker cell params:', params);
          if (params.row?.worker && typeof params.row.worker === 'object') {
            return <span>{params.row.worker.name || 'N/A'}</span>;
          }
          return <span>N/A</span>;
        }
      },
      { 
        field: 'payment_date', 
        headerName: 'Payment Date', 
        width: 130,
        renderCell: (params: any) => {
          console.log('Payment date params:', params);
          if (!params.value) return <span>-</span>;
          try {
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return <span>-</span>;
            
            // Format the date as DD-MM-YYYY
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return <span>{`${day}-${month}-${year}`}</span>;
          } catch (error) {
            console.error('Error formatting payment date:', error);
            return <span>-</span>;
          }
        }
      },
      { 
        field: 'amount', 
        headerName: 'Amount', 
        width: 130,
        renderCell: (params: any) => {
          console.log('Amount params:', params);
          const value = params.value;
          if (value === undefined || value === null) return <span>-</span>;
          return <span>{`₹${value.toFixed(2)}`}</span>;
        },
      },
      { field: 'status', headerName: 'Status', width: 130 },
      { 
        field: 'utr_number', 
        headerName: 'UTR Number', 
        width: 150,
        valueGetter: safeValueGetter((params: any) => params.row.utr_number || '-'),
      },
      { field: 'created_by', headerName: 'Created By', width: 130 },
      { 
        field: 'processed_by', 
        headerName: 'Processed By', 
        width: 130,
        valueGetter: safeValueGetter((params: any) => params.row.processed_by || '-'),
      },
    ];

    if (canDelete('payments')) {
      baseColumns.push({
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteClick(params.row.id)}
            showInMenu={false}
          />,
        ],
      });
    }

    return baseColumns;
  };

  const fetchData = async () => {
    try {
      const [paymentsRes, workersRes] = await Promise.all([
        api.get('/payments/part-payments'),
        api.get('/workers'),
      ]);

      console.log('Payments data received:', paymentsRes.data);
      if (paymentsRes.data && paymentsRes.data.length > 0) {
        console.log('First payment item:', paymentsRes.data[0]);
      }
      
      setPayments(paymentsRes.data);
      setActiveWorkers(workersRes.data.filter((worker: ActiveWorker) => worker.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = () => {
    setFormData(initialFormData);
    setWorkerPayments([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setWorkerPayments([]);
    setError(null);
  };

  const handleWorkerChange = async (_event: any, value: ActiveWorker | null) => {
    if (!value?.worker_id) return;

    if (workerPayments.some(wp => wp.worker_id === value.worker_id)) {
      setError('This worker is already added to the payment list');
      return;
    }

    try {
      const response = await api.get(`/payments/available-amount/${value.worker_id}`);
      setWorkerPayments(prev => [...prev, {
        worker_id: value.worker_id,
        amount: 0,
        availableAmount: response.data.availableAmount
      }]);
    } catch (error) {
      console.error('Error fetching available amount:', error);
      setError('Failed to fetch available amount');
    }
  };

  const handleAmountChange = (workerId: string, amount: number) => {
    setWorkerPayments(prev => prev.map(wp => 
      wp.worker_id === workerId 
        ? { ...wp, amount: Math.min(amount, wp.availableAmount) }
        : wp
    ));
  };

  const handleRemoveWorker = (workerId: string) => {
    setWorkerPayments(prev => prev.filter(wp => wp.worker_id !== workerId));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (workerPayments.length === 0) {
        setError('Please add at least one worker');
        return;
      }

      if (!formData.payment_date) {
        setError('Please select a payment date');
        return;
      }

      for (const wp of workerPayments) {
        if (wp.amount <= 0) {
          setError(`Please enter a valid amount for worker ${wp.worker_id}`);
          return;
        }
        if (wp.amount > wp.availableAmount) {
          setError(`Amount for worker ${wp.worker_id} exceeds available amount`);
          return;
        }
      }

      await Promise.all(workerPayments.map(wp =>
        api.post('/payments/part-payments', {
          worker_id: wp.worker_id,
          payment_date: formData.payment_date,
          amount: wp.amount,
          notes: formData.notes,
          created_by: user?.name || 'Unknown User',
        })
      ));

      await fetchData();
      handleClose();
    } catch (error: any) {
      console.error('Error creating payments:', error);
      setError(error.message || 'Failed to create payments');
    }
  };

  const handleDownloadPending = async () => {
    try {
      const response = await api.get('/payments/part-payments');
      const payments = response.data;
      const pendingPayments = payments.filter((p: PartPayment) => p.status === 'PENDING');

      const csvContent = [
        ['ID', 'Worker ID', 'Worker Name', 'Payment Date', 'Amount', 'Notes', 'UTR Number', 'Processed Date'],
        ...pendingPayments.map((p: PartPayment) => [
          p.id,
          p.worker_id,
          p.worker.name,
          new Date(p.payment_date).toLocaleDateString(),
          p.amount,
          p.notes || '',
          '',
          '',
        ]),
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_payments_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading payments:', error);
      setError('Failed to download payments');
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('payments_file', file);

      const response = await api.post('/payments/process-payments', formData);
      
      setSuccess(`Successfully processed ${response.data.processed.length} payments`);
      await fetchData();

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error processing payment file:', error);
      setError(error.response?.data?.error || 'Failed to process payment file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setPaymentToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (paymentToDelete === null) return;
    
    try {
      setLoading(true);
      await api.delete(`/payments/part-payments/${paymentToDelete}`);
      setSuccess('Payment successfully deleted');
      await fetchData();
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      setError(error.response?.data?.error || 'Failed to delete payment');
    } finally {
      setDeleteConfirmOpen(false);
      setPaymentToDelete(null);
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPaymentToDelete(null);
  };

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Part Payments</Typography>
        <Box>
          <Tooltip title="Download Pending Payments">
            <IconButton onClick={handleDownloadPending} sx={{ mr: 1 }}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload Processed Payments">
            <IconButton 
              onClick={handleUploadClick} 
              sx={{ mr: 1 }}
              disabled={uploadLoading}
            >
              {uploadLoading ? <CircularProgress size={24} /> : <UploadIcon />}
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Create Payment
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <DataGrid
        rows={payments}
        columns={makeSafeGridColumns(getColumns())}
        loading={loading}
        getRowId={(row) => row.id}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={(params) => {
          console.log("Row clicked:", params.row);
        }}
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Part Payments</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Autocomplete
              options={activeWorkers}
              getOptionLabel={(option) => `${option.worker_id} - ${option.name}`}
              onChange={handleWorkerChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add Worker"
                  required
                  error={!!error && error.includes('worker')}
                  helperText={error && error.includes('worker') ? error : ""}
                />
              )}
            />

            {workerPayments.map((wp) => {
              const worker = activeWorkers.find(w => w.worker_id === wp.worker_id);
              return (
                <Box key={wp.worker_id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography sx={{ flex: 1 }}>
                    {worker?.name} (Available: ₹{wp.availableAmount.toFixed(2)})
                  </Typography>
                  <TextField
                    label="Amount"
                    type="number"
                    value={wp.amount}
                    onChange={(e) => handleAmountChange(wp.worker_id, Number(e.target.value))}
                    sx={{ width: 150 }}
                    inputProps={{ 
                      min: 0,
                      max: wp.availableAmount,
                      step: 0.01
                    }}
                  />
                  <IconButton onClick={() => handleRemoveWorker(wp.worker_id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })}

            <TextField
              fullWidth
              name="payment_date"
              label="Payment Date"
              type="date"
              value={formData.payment_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              name="notes"
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Create Payments
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this payment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
      />
    </Box>
  );
} 